import { z } from 'zod';

import { zodResolver } from '@hookform/resolvers/zod';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Skeleton,
} from '@mui/material';
import {
  infrastructureRelationshipConfigDto,
  infrastructureTypes,
  itemCircuitDataDto,
  itemInfrastructureDto,
  itemStatus,
} from '@nohara-dxdev/mt-common';
import {
  convertJsonSchemaToDefaultValues,
  convertJsonSchemaToZod,
} from '@nohara-dxdev/mt-json-schema';
import { FieldProps } from '@rjsf/utils';
import { useEffect, useMemo } from 'react';
import { FormContainer, useForm, useFormState } from 'react-hook-form-mui';
import { useTranslation } from 'react-i18next';
import { useLatest } from 'react-use';

import { useSettingValues } from '@/features/settings';
import { ItemTemplateEntityWithSchema } from '@/types/api';
import { Location, LocationType } from '@/types/location';

import { useItemFilterValues } from '../../providers';
import { getRoleTagTranslationKey } from '../../utils/project-role-tags';
import { RequiredInfrastructureDisplay } from '../required-infrastructure-display';

import { ItemAttributesField } from './item-attributes-field';
import { ItemInfoForm } from './item-info-form';
import { ItemInfrastructureForm } from './item-infrastructure-form';
import { ItemInfrastructureFormPreview } from './item-infrastructure-form-preview';
import { ItemFormData } from './types';

export interface ItemTemplateDetailProps {
  itemTemplate: Pick<
    ItemTemplateEntityWithSchema,
    | 'name'
    | 'templateKey'
    | 'jsonSchema'
    | 'uiSchema'
    | 'itemTypeId'
    | 'constructionTypeIds'
    | 'isBoardItem'
  > &
    Partial<
      Pick<
        ItemTemplateEntityWithSchema,
        'lastItemNumber' | 'projectRoleTags' | 'infrastructureRequirements' | 'itemTemplateVersion'
      >
    >;
  initialData?: ItemFormData;
  onChange?: (data: ItemFormData) => void;
  onDirtyChange?: (isDirty: boolean) => void;
  mode?: 'editMode' | 'previewMode';
  formId?: string;
}

export const DefaultItemForm = ({
  itemTemplate,
  onChange,
  onDirtyChange,
  onSubmit,
  disabled,
  initialData,
  mode = 'editMode',
  formId,
}: Partial<Pick<FieldProps, 'onSubmit' | 'disabled'>> & ItemTemplateDetailProps) => {
  const { t } = useTranslation();
  const { getSettingEntityValue, isLoading: isSettingsLoading } = useSettingValues();
  const filterValues = useItemFilterValues();

  // Extract values from filterModel array for backward compatibility
  // Search through all filterModels to find the first matching field
  const constructionTypeId = useMemo(() => {
    if (!filterValues.filterModel) return undefined;
    for (const filterModel of filterValues.filterModel) {
      const item = filterModel.items.find((item) => item.field === 'constructionTypeId');
      if (item) {
        return item.value as string | undefined;
      }
    }
    return undefined;
  }, [filterValues.filterModel]);

  const systemIds = useMemo(() => {
    if (!filterValues.filterModel) return undefined;
    for (const filterModel of filterValues.filterModel) {
      const item = filterModel.items.find((item) => item.field === 'systemIds');
      if (item) {
        return item.value as string[] | undefined;
      }
    }
    return undefined;
  }, [filterValues.filterModel]);

  const locationId = filterValues.locationIdOrAdjacentLocationIdEq;

  const isPreviewMode = mode === 'previewMode';

  const { jsonSchema } = itemTemplate;

  const isBuildingFilterConstrained = Boolean(locationId);

  const isConstructionTypeFilterConstrained = Boolean(
    constructionTypeId && itemTemplate.constructionTypeIds.includes(constructionTypeId),
  );

  const isSystemFilterConstrained = Boolean(
    systemIds?.length && isConstructionTypeFilterConstrained,
  );

  const defaultValues = useMemo(() => {
    const baseValues = {
      name: '',
      location:
        isBuildingFilterConstrained && locationId
          ? ({ id: locationId, type: LocationType.ROOM } as Location)
          : (initialData?.location ?? null),
      adjacentLocation: initialData?.adjacentLocation ?? null,
      constructionTypeId: isConstructionTypeFilterConstrained ? constructionTypeId : undefined,
      systemIds: isSystemFilterConstrained ? systemIds : undefined,
      status: initialData?.status ?? itemStatus.inProgress,
      attributes: convertJsonSchemaToDefaultValues(itemTemplate.jsonSchema),
      infrastructures: [],
      circuits: [],
      infrastructureRelationshipConfigs: [],
    };

    const defaultV = {
      ...baseValues,
      ...initialData,
      // Auto-generate itemNumber if not provided in initialData
      itemNumber:
        initialData?.itemNumber ??
        `${itemTemplate.templateKey}-${(itemTemplate.lastItemNumber ?? 0) + 1}`,
    };
    return {
      ...defaultV,
      infrastructureRelationshipConfigs: infrastructureTypes.map((type) => {
        const config = defaultV.infrastructureRelationshipConfigs.find(
          (c) => c.infrastructureType === type,
        );
        return (
          config ?? {
            infrastructureType: type,
            load: 0,
          }
        );
      }),
    };
  }, [
    itemTemplate.templateKey,
    itemTemplate.lastItemNumber,
    itemTemplate.jsonSchema,
    constructionTypeId,
    systemIds,
    isConstructionTypeFilterConstrained,
    isSystemFilterConstrained,
    isBuildingFilterConstrained,
    locationId,
    initialData,
  ]);

  const onChangeRef = useLatest(onChange);
  const onDirtyChangeRef = useLatest(onDirtyChange);

  const zodSchema = useMemo(() => {
    const apiLocationTypeValues = [
      LocationType.ROOM,
      LocationType.OUTDOOR,
      LocationType.OTHER_SPACES,
    ] as [LocationType, ...LocationType[]];

    return z
      .object({
        name: z.string().min(1),
        location: z
          .object({
            id: z.uuidv4(),
            type: z.enum(apiLocationTypeValues),
          })
          .nullable()
          .optional(),
        adjacentLocation: z
          .object({
            id: z.uuidv4(),
            type: z.enum(apiLocationTypeValues),
          })
          .nullable()
          .optional(),
        constructionTypeId: z
          .union([z.uuidv4(), z.literal('')])
          .optional()
          .nullable()
          .transform((val) => (val === '' ? null : val)),
        systemIds: z.array(z.uuidv4()).optional().default([]),
        status: z.enum(Object.values(itemStatus)).optional(),
        locationTypeId: z
          .union([z.uuidv4(), z.literal('')])
          .optional()
          .nullish(),
        quotationTypeId: z
          .union([z.uuidv4(), z.literal('')])
          .optional()
          .nullish(),
        attributes: convertJsonSchemaToZod(itemTemplate.jsonSchema as any),
        infrastructures: z.array(itemInfrastructureDto).optional().default([]),
        circuits: z.array(itemCircuitDataDto).optional().default([]),
        itemNumber: z.string().optional(),
        infrastructureRelationshipConfigs: z
          .array(infrastructureRelationshipConfigDto)
          .optional()
          .default([]),
      })
      .refine(
        (data) => {
          // adjacentLocation cannot equal location
          if (
            data.adjacentLocation &&
            data.location &&
            data.adjacentLocation.id === data.location.id
          ) {
            return false;
          }
          return true;
        },
        {
          message: 'adjacentLocation must be different from location',
          path: ['adjacentLocation'],
        },
      )
      .strip();
  }, [itemTemplate.jsonSchema]);

  const form = useForm<ItemFormData>({
    resolver: zodResolver(zodSchema as any),
    defaultValues,
    disabled: disabled ?? isPreviewMode,
  });

  // Run a command when the form changes
  useEffect(() => {
    const subscription = form.watch((values) => {
      onChangeRef.current?.(values as ItemFormData);
    });
    return () => {
      subscription.unsubscribe();
    };
  }, [form, onChangeRef]);

  // useFormState keeps isDirty fresh; reading form.formState.isDirty in watch() above can be stale.
  const { isDirty } = useFormState({ control: form.control });
  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty);
  }, [isDirty, onDirtyChangeRef]);

  useEffect(() => {
    // Preserve in-progress edits and isDirty across a background data refetch.
    form.reset(defaultValues, { keepDirtyValues: true, keepDirty: true });
  }, [defaultValues, form.reset]);

  // Map role tags to translated names
  const projectRoleNames = useMemo(() => {
    return itemTemplate.projectRoleTags?.length
      ? itemTemplate.projectRoleTags.map((tag) => t(getRoleTagTranslationKey(tag)))
      : [];
  }, [itemTemplate.projectRoleTags, t]);

  const onError = (errors: any) => {
    console.log(errors);
  };

  return (
    <FormContainer
      formContext={form as any}
      onSuccess={onSubmit}
      onError={onError}
      FormProps={{ id: formId }}
    >
      <Stack spacing={3}>
        {/* Template Information */}
        <Card>
          <CardContent>
            <Stack spacing={3}>
              {/* Basic Template Info Row */}
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('itemTemplates.detail.templateName')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {itemTemplate.name}
                  </Typography>
                  {itemTemplate.itemTemplateVersion != null && (
                    <Chip
                      label={t('itemTemplates.version.label', {
                        version: itemTemplate.itemTemplateVersion,
                      })}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('itemTemplates.detail.templateCharacter')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {itemTemplate.templateKey}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 200 }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    {t('itemTemplates.detail.itemType')}
                  </Typography>
                  {isSettingsLoading ? (
                    <Skeleton variant="rounded" width={80} height={24} />
                  ) : (
                    <Typography variant="body1" fontWeight="medium">
                      {getSettingEntityValue<string>(itemTemplate.itemTypeId)}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {/* Project Roles */}
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {t('itemTemplates.detail.projectRoles')}
                </Typography>
                {projectRoleNames.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {projectRoleNames.map((role, index) => (
                      <Chip
                        key={index}
                        label={role}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    {t('itemTemplates.detail.noRolesAssigned')}
                  </Typography>
                )}
              </Box>

              {/* Type of Construction */}
              <Box>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  {t('itemTemplates.detail.constructionTypes')}
                </Typography>

                {isSettingsLoading ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {[...Array(2)].map((_, idx) => (
                      <Skeleton
                        key={idx}
                        variant="rounded"
                        width={80}
                        height={32}
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                ) : itemTemplate.constructionTypeIds.length > 0 ? (
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {itemTemplate.constructionTypeIds.map((id: string, index: number) => (
                      <Chip
                        key={index}
                        label={getSettingEntityValue<string>(id)}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    {t('itemTemplates.detail.noConstructionTypes')}
                  </Typography>
                )}
              </Box>

              {/* Required Infrastructure */}
              {itemTemplate.infrastructureRequirements &&
                itemTemplate.infrastructureRequirements.length > 0 && (
                  <RequiredInfrastructureDisplay
                    infrastructureRequirements={itemTemplate.infrastructureRequirements}
                  />
                )}
            </Stack>
          </CardContent>
        </Card>

        {/* Item Details Form */}
        <Card>
          <CardContent>
            <ItemInfoForm itemTemplate={itemTemplate} mode={mode} />
          </CardContent>
        </Card>

        {/* Attributes Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">{t('itemTemplates.schemaDetail.attributes')}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ItemAttributesField schema={jsonSchema} />
          </AccordionDetails>
        </Accordion>

        {/* Infrastructure Section */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Infrastructure</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {isPreviewMode ? (
              <ItemInfrastructureFormPreview infrastructures={[]} circuits={[]} />
            ) : (
              <ItemInfrastructureForm requirements={itemTemplate.infrastructureRequirements} />
            )}
          </AccordionDetails>
        </Accordion>
      </Stack>
    </FormContainer>
  );
};
